import SaveAsTemplatePanel from '@/components/templates/SaveAsTemplatePanel';
import TemplateComposerControls from '@/components/templates/TemplateComposerControls';
import { useAuth } from '@/contexts/auth-context';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import type { InvitationMethod } from '@/types/invitation';
import type { useEmailAttachments } from '@/hooks/use-email-attachments';
import type { TemplatePair } from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  resolveInvitationEditorContent,
  resolveInvitationEditorKey,
} from '../templates/invitation-editor-content';
import { useInvitationTemplateFlow } from '../templates/use-invitation-template-flow';
import { useSeedEmissionGuard } from '../templates/use-seed-emission-guard';
import { useOutreachLanguage } from './use-outreach-language';
import { useOutreachEmailSubject, type OutreachSubjectKind } from './use-outreach-subject';
import { useOutreachSubjectState } from './use-outreach-subject-state';

interface UseOutreachComposerAssemblyArgs {
  /** Brand id gate: undefined disables the library fetch (no usable mailbox). */
  brandId: string | undefined;
  /** Library scope: an explicit (channel, context) pair (sponsored flows)... */
  pair?: TemplatePair | null;
  /** ...or the invitation method it derives from (affiliation flows). */
  method?: InvitationMethod;
  /** Base body when the store is untouched and no library entry is selected. */
  fallbackContent: string | null;
  /** Offered in the subject too, not just the body: same set, one dropdown. */
  availableVariables: AvailableVariable[];
  /** Disables the template selector while the caller's body source loads. */
  controlsDisabled?: boolean;
  subjectKind?: OutreachSubjectKind;
  /**
   * Server-rendered Italian subject (`prepared.defaultSubjects.it`). Used only
   * when the body language is Italian; en/fr keep their bundled defaults.
   */
  italianSubject?: string;
  attachmentControls?: ReturnType<typeof useEmailAttachments>;
}

interface UseOutreachComposerAssemblyResult {
  /** Remount key for the composer (language + selected library entry). */
  composerKey: string;
  /** Resolved body: selected entry > manual edits > fallback. */
  editorContent: string | null;
  /** Template selector + save action, for the composer's `editorFooter` slot. */
  editorFooter: ReactNode;
  /** Save-as-template overlay; render after the composer. */
  saveAsTemplatePanel: ReactNode;
  /** Editable subject line with the variable dropdown; render above the composer. */
  subjectRow: ReactNode;
  /** Live subject to ship: the localized default until the brand edits it. */
  subject: string;
  /** False on an empty subject: the caller must block the send. */
  isSubjectValid: boolean;
  /** True while the store body still matches the loaded template. */
  isEditorUntouched: boolean;
  /** Mirror one composer emission into the store (seed guard applied). */
  registerEmission: (snapshot: string | null) => void;
  /** Persisted library template provenance; null for the system fallback. */
  baseTemplateId: string | null;
}

/**
 * Assembly of an outreach email composer around the invitation store: the
 * seed-emission guard, the template-library flow (selector + save-as-template,
 * manager-gated), the body/key resolution and the editable subject row. Shared
 * by the affiliation email step and the sponsored compose module so the wiring
 * can never drift between them — each caller keeps only its body source, its
 * required link variable and its send.
 */
export const useOutreachComposerAssembly = ({
  brandId,
  pair,
  method,
  fallbackContent,
  availableVariables,
  controlsDisabled,
  subjectKind,
  italianSubject,
  attachmentControls,
}: UseOutreachComposerAssemblyArgs): UseOutreachComposerAssemblyResult => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedLanguage } = useOutreachLanguage();

  // Store mirror + first-emission guard: a remount over an edited body never
  // rebaselines the original (see the hook's contract).
  const { isEditorUntouched, messageSnapshot, registerEmission } = useSeedEmissionGuard();

  const templateFlow = useInvitationTemplateFlow({
    brandId,
    pair,
    method,
    language: selectedLanguage,
    isEditorUntouched,
    onApplyAttachments: attachmentControls?.replaceAttachments,
  });

  // Saving a template is a management action (owner/admin); selecting one stays
  // available to anyone who can send. Save only surfaces once the body diverges
  // from the loaded template — saving an untouched copy is pointless.
  const { canManage: canManageTemplates } = useIsBrandManager();
  const showSaveTemplate =
    canManageTemplates && (!isEditorUntouched || templateFlow.attachmentsCustomized);

  // Body precedence (shared helper): selected library entry > manual edits >
  // fallback. The key remounts the composer when the language or the selected
  // entry changes, so the editor re-seeds from the freshly resolved body.
  const editorContent = resolveInvitationEditorContent({
    isEditorUntouched,
    selectedEntryId: templateFlow.selectedEntryId,
    messageSnapshot,
    fallbackContent,
  });
  const composerKey = resolveInvitationEditorKey({
    language: selectedLanguage,
    selectedEntryId: templateFlow.selectedEntryId,
  });

  // Default subject: the localized copy with the brand name interpolated as
  // literal text. Seeding a `{{brand_name}}` chip instead is not an option —
  // i18next would interpolate the token itself. Chips stay additive: the brand
  // can insert any variable on top (typically {{creator_first_name}}).
  const defaultSubject = useOutreachEmailSubject(
    user?.currentBrand?.name ?? '',
    subjectKind,
    italianSubject
  );
  const { subjectRow, subject, isSubjectValid } = useOutreachSubjectState(
    defaultSubject,
    availableVariables
  );

  const registerComposerEmission = useCallback(
    (snapshot: string | null) => registerEmission(snapshot, composerKey),
    [registerEmission, composerKey]
  );

  const editorFooter = (
    <TemplateComposerControls
      entries={templateFlow.entries}
      selectedEntryId={templateFlow.selectedEntryId}
      onSelect={templateFlow.selectEntry}
      disabled={controlsDisabled || templateFlow.attachmentsBusy}
      saveDisabled={templateFlow.attachmentsBlocked}
      confirmAttachmentReplacement={templateFlow.attachmentsCustomized}
      isLoading={templateFlow.isLoading}
      showSave={showSaveTemplate}
      onSave={templateFlow.openSaveDrawer}
      saveLabel={t('invitations.modal.saveTemplate.action')}
    />
  );

  const saveAsTemplatePanel = templateFlow.saveSeed ? (
    <SaveAsTemplatePanel
      open={templateFlow.isSaveDrawerOpen}
      seed={templateFlow.saveSeed}
      onClose={templateFlow.closeSaveDrawer}
      onSaved={templateFlow.onTemplateSaved}
      showBack
    />
  ) : null;

  return {
    composerKey,
    editorContent,
    editorFooter,
    saveAsTemplatePanel,
    subjectRow,
    subject,
    isSubjectValid,
    isEditorUntouched,
    registerEmission: registerComposerEmission,
    baseTemplateId: templateFlow.selectedTemplateId,
  };
};
